import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';
import type { StickerItem, StickerPack } from './LiveStickers';
import { packPriceLabel } from '../utils/stickerPackPricing';

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

type PresignResponse = {
  stickerId: string;
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
};

const CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, #334155 25%, transparent 25%), linear-gradient(-45deg, #334155 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #334155 75%), linear-gradient(-45deg, transparent 75%, #334155 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
};

/**
 * Edits an existing GLOBAL catalog sticker: rename, move between packs, show/hide, replace the
 * image in place, or copy its CDN link — the sticker equivalent of EditLiveFrame.tsx.
 *
 * The sticker (and the pack list, for the move-pack dropdown) is read from router state, passed by
 * LiveStickers.tsx which already has both from the catalog fetch — there is no
 * `GET /v1/live/admin/stickers/:id`. A hard refresh or a shared link with no state falls back to
 * fetching the whole admin catalog and finding the id by scanning every pack's items, same fallback
 * shape as EditLiveFrame's page-by-page frame search.
 */
const EditLiveSticker: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stickerId: routeStickerId } = useParams<{ stickerId: string }>();

  const [loadingSticker, setLoadingSticker] = useState(true);
  const [sticker, setSticker] = useState<StickerItem | null>(
    (location.state as { sticker?: StickerItem } | null)?.sticker ?? null
  );
  const [packs, setPacks] = useState<StickerPack[]>((location.state as { packs?: StickerPack[] } | null)?.packs ?? []);

  const [name, setName] = useState(sticker?.name ?? '');
  const [packId, setPackId] = useState(sticker?.packId ?? '');
  const [saving, setSaving] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  const [imagePreview, setImagePreview] = useState('');
  const [imageError, setImageError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [presign, setPresign] = useState<PresignResponse | null>(null);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  // Fallback path: no router state (direct link / refresh). Load the admin catalog and scan every
  // pack's items for the id — there is no single-sticker GET, mirroring EditLiveFrame's fallback.
  useEffect(() => {
    if (sticker || !routeStickerId) {
      setLoadingSticker(false);
      return;
    }
    const headers = headerWithAuth();
    if (!headers) {
      setLoadingSticker(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${liveApiBase}/admin/sticker-packs`, { headers });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to load stickers');
        const allPacks = (json.data?.packs as StickerPack[]) || [];
        if (cancelled) return;
        setPacks(allPacks);
        for (const p of allPacks) {
          const found = p.items.find((it) => it.id === routeStickerId);
          if (found) {
            setSticker(found);
            setName(found.name);
            setPackId(found.packId);
            break;
          }
        }
      } catch (error) {
        if (!cancelled) toast.error((error as Error).message || 'Failed to load sticker');
      } finally {
        if (!cancelled) setLoadingSticker(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeStickerId]);

  useEffect(() => {
    if (sticker) {
      setName(sticker.name);
      setPackId(sticker.packId);
    }
  }, [sticker]);

  useEffect(
    () => () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleFileChange = (file: File | null) => {
    setPresign(null);
    setImageError('');
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    if (!file) {
      setImagePreview('');
      return;
    }
    // PNG and WebP only — the backend rejects anything else, because a format without an alpha
    // channel renders as an opaque box sitting over the video.
    if (!/image\/(png|webp)/.test(file.type)) {
      setImageError('Stickers must be PNG or WebP with alpha. A JPEG renders as a white box over the video.');
      setImagePreview('');
      return;
    }
    setImagePreview(URL.createObjectURL(file));
  };

  /**
   * Presigns and uploads the replacement image immediately on selection, passing the EXISTING
   * stickerId so the backend overwrites the same R2 object and the same catalog row (rather than
   * minting a new sticker the old one leaves orphaned) — same shape as EditLiveFrame's
   * uploadReplacement.
   */
  const uploadReplacement = async (file: File) => {
    if (!sticker) return;
    const headers = headerWithAuth();
    if (!headers) return;

    setUploading(true);
    try {
      const contentType = file.type === 'image/webp' ? 'image/webp' : 'image/png';
      const presignRes = await fetch(`${liveApiBase}/admin/stickers/presign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ contentType, stickerId: sticker.id }),
      });
      const presignJson = await presignRes.json();
      if (!presignRes.ok) throw new Error(presignJson.message || 'Failed to get upload URL');
      const result = presignJson.data as PresignResponse;

      const uploadRes = await fetch(result.uploadUrl, {
        method: 'PUT',
        body: await file.arrayBuffer(),
        headers: { 'Content-Type': contentType },
      });
      if (!uploadRes.ok) throw new Error('Failed to upload image');

      setPresign(result);
      toast.success('Image uploaded — Save Changes to apply.');
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
      setImagePreview('');
    } finally {
      setUploading(false);
    }
  };

  const onFileSelected = (file: File | null) => {
    handleFileChange(file);
    if (file && /image\/(png|webp)/.test(file.type)) {
      void uploadReplacement(file);
    }
  };

  const canSave = Boolean(sticker) && name.trim().length > 0 && !imageError && !uploading && !saving;

  // Premium status belongs to the pack, so it follows the pack picker: moving a sticker into a paid
  // pack makes it premium, and moving it into a free pack makes it free.
  const currentPack = packs.find((p) => p.id === sticker?.packId);
  const selectedPack = packs.find((p) => p.id === packId);
  const isPremium = selectedPack ? selectedPack.isPremium === true : sticker?.isPremium === true;
  const premiumChanges = Boolean(currentPack && selectedPack) && (currentPack?.isPremium === true) !== (selectedPack?.isPremium === true);

  const handleSave = async () => {
    if (!sticker) return;
    if (premiumChanges && selectedPack) {
      const message = selectedPack.isPremium
        ? `"${selectedPack.name}" is a paid pack (${packPriceLabel(selectedPack)}). Moving this sticker there makes it premium: free for paying Plus subscribers, everyone else must buy the pack.`
        : `"${selectedPack.name}" is a free pack. Moving this sticker there makes it free for everyone.`;
      if (!window.confirm(`${message}\n\nContinue?`)) return;
    }
    const headers = headerWithAuth();
    if (!headers) return;

    setSaving(true);
    try {
      // If the image was replaced, adminSaveSticker (an upsert keyed on stickerId) both finalizes
      // the new fileKey into the catalog row and lets us update the name/pack in the same call —
      // no separate PATCH needed on that path.
      if (presign) {
        const res = await fetch(`${liveApiBase}/admin/stickers`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            stickerId: sticker.id,
            packId,
            name: name.trim(),
            fileKey: presign.fileKey,
            kind: sticker.kind,
            aspectRatio: sticker.aspectRatio,
            sortOrder: sticker.sortOrder,
            isActive: sticker.isActive,
            textSlot: sticker.textSlot ?? null,
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to save sticker');
        toast.success('Sticker updated');
      } else {
        const patch: Record<string, unknown> = {};
        if (name.trim() !== sticker.name) patch.name = name.trim();
        if (packId !== sticker.packId) patch.packId = packId;

        if (Object.keys(patch).length === 0) {
          toast.info('Nothing changed.');
          setSaving(false);
          return;
        }

        const res = await fetch(`${liveApiBase}/admin/stickers/${encodeURIComponent(sticker.id)}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify(patch),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to save sticker');
        toast.success('Sticker updated');
      }
      navigate('/live-stickers');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save sticker');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!sticker) return;
    const headers = headerWithAuth();
    if (!headers) return;

    setTogglingActive(true);
    try {
      const res = await fetch(`${liveApiBase}/admin/stickers/${encodeURIComponent(sticker.id)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ isActive: !sticker.isActive }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update sticker');
      setSticker((prev) => (prev ? { ...prev, isActive: !prev.isActive } : prev));
      toast.success(!sticker.isActive ? 'Sticker shown' : 'Sticker hidden');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to update sticker');
    } finally {
      setTogglingActive(false);
    }
  };

  const handleCopyLink = async () => {
    if (!sticker) return;
    try {
      await navigator.clipboard.writeText(sticker.imageUrl);
      toast.success('Link copied');
    } catch {
      // Clipboard API can be blocked (permissions, non-HTTPS context); fall back to a manual
      // copy path instead of leaving the admin with nothing.
      window.prompt('Copy this link:', sticker.imageUrl);
    }
  };

  const handleDelete = async () => {
    if (!sticker) return;
    if (!window.confirm(`Delete "${sticker.name}"? This cannot be undone.`)) return;
    const headers = headerWithAuth();
    if (!headers) return;

    setSaving(true);
    try {
      const res = await fetch(`${liveApiBase}/admin/stickers/${encodeURIComponent(sticker.id)}`, {
        method: 'DELETE',
        headers,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to delete sticker');
      toast.success('Sticker deleted');
      navigate('/live-stickers');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete sticker');
      setSaving(false);
    }
  };

  if (loadingSticker) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-sm text-gray-400'>Loading sticker...</div>
      </div>
    );
  }

  if (!sticker) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-4'>
          <p className='text-sm text-gray-400'>Sticker not found.</p>
          <button
            onClick={() => navigate('/live-stickers')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
          >
            Back to Stickers
          </button>
        </div>
      </div>
    );
  }

  const displayImage = imagePreview || sticker.imageUrl;

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Edit Sticker</h1>
            <p className='text-sm text-gray-400'>Rename, move pack, replace the image, or copy its link.</p>
          </div>
          <button
            onClick={() => navigate('/live-stickers')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
          >
            Back to Stickers
          </button>
        </div>

        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-5'>
          <div className='flex items-center justify-between'>
            <div className='text-xs text-gray-500 font-mono'>ID: {sticker.id}</div>
            <div className='flex items-center gap-2'>
            {/* Read-only: a sticker has no premium setting of its own. */}
            <span
              title='Set on the pack, from Pricing on the Live Stickers page'
              className={`px-2 py-0.5 text-xs rounded-full border ${
                isPremium
                  ? 'text-amber-300 border-amber-400/40 bg-amber-500/10'
                  : 'text-gray-300 border-white/20 bg-white/5'
              }`}
            >
              {isPremium ? 'Premium (from pack)' : 'Free (from pack)'}
            </span>
            <span
              className={`px-2 py-0.5 text-xs rounded-full border ${
                sticker.isActive
                  ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
                  : 'text-red-300 border-red-400/40 bg-red-500/10'
              }`}
            >
              {sticker.isActive ? 'Active' : 'Hidden'}
            </span>
            </div>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
            <div>
              <p className='text-[10px] uppercase tracking-wider text-gray-500 mb-1.5'>
                {presign ? 'New (will save)' : 'Image'}
              </p>
              <div
                className='h-40 flex items-center justify-center rounded-lg border border-white/10 p-3'
                style={CHECKERBOARD_STYLE}
              >
                <img src={displayImage} alt={sticker.name} className='max-h-full max-w-full object-contain' />
              </div>
              <input
                type='file'
                accept='image/png,image/webp'
                disabled={uploading}
                onChange={(e) => onFileSelected(e.target.files?.[0] || null)}
                className='mt-2 block w-full text-xs text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-white/10 file:text-white hover:file:bg-white/20 disabled:opacity-50'
              />
              {uploading && <p className='mt-1.5 text-xs text-gray-400'>Uploading...</p>}
              {imageError && (
                <div className='mt-1.5 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-200'>
                  {imageError}
                </div>
              )}
            </div>

            <div className='space-y-4'>
              <div>
                <label className='block text-xs text-gray-400 mb-1'>Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]/60'
                />
              </div>

              <div>
                <label className='block text-xs text-gray-400 mb-1'>Pack</label>
                <select
                  value={packId}
                  onChange={(e) => setPackId(e.target.value)}
                  className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]/60'
                >
                  {packs.map((p) => (
                    <option key={p.id} value={p.id} className='bg-slate-800'>
                      {p.name} ({packPriceLabel(p)})
                    </option>
                  ))}
                  {/* The sticker's current pack may not be in a stale `packs` list (e.g. loaded from
                      router state before a rename) — keep it selectable either way. */}
                  {!packs.some((p) => p.id === sticker.packId) && (
                    <option value={sticker.packId} className='bg-slate-800'>
                      {sticker.packId}
                    </option>
                  )}
                </select>
              </div>

              <div className='flex items-center gap-1.5 flex-wrap text-xs'>
                <span className='px-1.5 py-0.5 rounded bg-white/10 text-gray-300'>{sticker.kind}</span>
                {sticker.kind === 'text' && (
                  <span className='px-1.5 py-0.5 rounded bg-white/10 text-gray-400'>
                    has a text slot — edit its geometry from Upload Sticker
                  </span>
                )}
              </div>

              <div className='pt-1'>
                <p className='text-[10px] uppercase tracking-wider text-gray-500 mb-1.5'>CDN link</p>
                <div className='flex gap-2'>
                  <input
                    readOnly
                    value={sticker.imageUrl}
                    onFocus={(e) => e.currentTarget.select()}
                    className='flex-1 min-w-0 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-300 truncate'
                  />
                  <button
                    onClick={handleCopyLink}
                    className='shrink-0 px-3 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className='pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-t border-white/10 mt-2'>
            <div className='flex gap-2'>
              <button
                onClick={handleToggleActive}
                disabled={togglingActive}
                className='px-4 py-2.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
              >
                {togglingActive ? 'Saving...' : sticker.isActive ? 'Hide' : 'Show'}
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className='px-4 py-2.5 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-40'
              >
                Delete
              </button>
            </div>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className='px-6 py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
            >
              {saving ? 'Saving...' : uploading ? 'Uploading...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLiveSticker;
